export type Point = readonly [number, number];

export const clamp = (v: number, min = 0, max = 1): number => Math.min(max, Math.max(min, v));

export function seededRandom(seed: number): () => number {
  let t = seed % 2147483647;
  if (t <= 0) t += 2147483646;
  return () => {
    t = (t * 16807) % 2147483647;
    return (t - 1) / 2147483646;
  };
}

export function roundedPath(points: readonly Point[], radius: number): string {
  if (points.length < 2) return "";
  const [x0, y0] = points[0] as Point;
  let d = `M${fmt(x0)} ${fmt(y0)}`;
  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i - 1] as Point;
    const [cx, cy] = points[i] as Point;
    const [nx, ny] = points[i + 1] as Point;
    const inLen = Math.hypot(cx - px, cy - py);
    const outLen = Math.hypot(nx - cx, ny - cy);
    const r = Math.min(radius, inLen / 2, outLen / 2);
    const ix = (cx - px) / (inLen || 1);
    const iy = (cy - py) / (inLen || 1);
    const ox = (nx - cx) / (outLen || 1);
    const oy = (ny - cy) / (outLen || 1);
    const sx = cx - ix * r;
    const sy = cy - iy * r;
    const ex = cx + ox * r;
    const ey = cy + oy * r;
    const sweep = ix * oy - iy * ox > 0 ? 1 : 0;
    d += ` L${fmt(sx)} ${fmt(sy)} A${fmt(r)} ${fmt(r)} 0 0 ${sweep} ${fmt(ex)} ${fmt(ey)}`;
  }
  const [lx, ly] = points[points.length - 1] as Point;
  return `${d} L${fmt(lx)} ${fmt(ly)}`;
}

export function shiftPoints(points: readonly Point[], dx: number, dy: number): Point[] {
  return points.map(([x, y]) => [x + dx, y + dy] as Point);
}

const fmt = (n: number): string => String(Math.round(n * 100) / 100);

export function wavePath(
  width: number,
  height: number,
  level: number,
  amplitude: number,
  crests: number,
  phase: number,
  steps = 96,
): string {
  const base = height - clamp(level) * height;
  let d = `M0 ${fmt(height)}`;
  for (let i = 0; i <= steps; i++) {
    const x = (i / steps) * width;
    const y = base + amplitude * Math.sin((i / steps) * crests * Math.PI * 2 + phase);
    d += ` L${fmt(x)} ${fmt(Math.min(height, y))}`;
  }
  return `${d} L${fmt(width)} ${fmt(height)} Z`;
}

export interface Sample {
  x: number;
  y: number;
  nx: number;
  ny: number;
  s: number;
}

export function sampleCurve(points: readonly Point[], perSegment = 24): Sample[] {
  const out: Sample[] = [];
  const pts = [points[0], ...points, points[points.length - 1]] as Point[];
  for (let i = 1; i < pts.length - 2; i++) {
    const [p0, p1, p2, p3] = [pts[i - 1], pts[i], pts[i + 1], pts[i + 2]] as Point[] as [
      Point,
      Point,
      Point,
      Point,
    ];
    for (let j = 0; j < perSegment; j++) {
      const t = j / perSegment;
      const t2 = t * t;
      const t3 = t2 * t;
      const at = (a: number, b: number, c: number, d: number) =>
        0.5 *
        (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      const dt = (a: number, b: number, c: number, d: number) =>
        0.5 * (-a + c + 2 * (2 * a - 5 * b + 4 * c - d) * t + 3 * (-a + 3 * b - 3 * c + d) * t2);
      const x = at(p0[0], p1[0], p2[0], p3[0]);
      const y = at(p0[1], p1[1], p2[1], p3[1]);
      const tx = dt(p0[0], p1[0], p2[0], p3[0]);
      const ty = dt(p0[1], p1[1], p2[1], p3[1]);
      const len = Math.hypot(tx, ty) || 1;
      const prev = out[out.length - 1];
      const s = prev ? prev.s + Math.hypot(x - prev.x, y - prev.y) : 0;
      out.push({ x, y, nx: -ty / len, ny: tx / len, s });
    }
  }
  const last = points[points.length - 1] as Point;
  const prev = out[out.length - 1];
  if (prev)
    out.push({
      ...prev,
      x: last[0],
      y: last[1],
      s: prev.s + Math.hypot(last[0] - prev.x, last[1] - prev.y),
    });
  return out;
}

export function streamPath(
  samples: readonly Sample[],
  offset: number,
  amplitude: number,
  wavelength: number,
  phase: number,
): string {
  let d = "";
  samples.forEach((p, i) => {
    const w = offset + amplitude * Math.sin((p.s / wavelength) * Math.PI * 2 + phase);
    d += `${i ? " L" : "M"}${fmt(p.x + p.nx * w)} ${fmt(p.y + p.ny * w)}`;
  });
  return d;
}
